import React from 'react';
import { format } from 'date-fns';
import { MessageSquare, Heart, Send, Edit2, Trash2, Download, Image, File, FileText, Paperclip } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import supabase from "../lib/supabase";
import Button from './Button';
import UserName from './UserName';
import { Link } from 'react-router-dom';
import { useAttachments } from '../utils/attachmentUtils';

interface PostCardProps {
  post: any;
  onUpdate?: () => void;
  onDelete?: () => void;
}

export default function PostCard({ post, onUpdate, onDelete }: PostCardProps) {
  const { user } = useAuthStore();
  const [isLiking, setIsLiking] = React.useState(false);
  const [isEditing, setIsEditing] = React.useState(false);
  const [editContent, setEditContent] = React.useState(post.content);
  const [editTitle, setEditTitle] = React.useState(post.title || '');
  const [localPost, setLocalPost] = React.useState(post);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [expandedPost, setExpandedPost] = React.useState(false);
  const [replies, setReplies] = React.useState<any[]>([]);
  const [newReply, setNewReply] = React.useState('');
  const [editingReplyId, setEditingReplyId] = React.useState<string | null>(null);
  const [editReplyContent, setEditReplyContent] = React.useState('');

  React.useEffect(() => {
    setLocalPost(post);
    setEditContent(post.content);
    setEditTitle(post.title || '');
  }, [post]);

  const renderAttachment = (attachment: any) => {
    const isImage = attachment.type.startsWith('image/');
    const isPdf = attachment.type === 'application/pdf';
    const isText = attachment.type.includes('text') || attachment.type.includes('document');
    const displayUrl = attachment.signedUrl || attachment.url;
    
    return (
      <div key={attachment.path} className="mt-2 max-w-xs">
        {isImage ? (
          <div className="relative group">
            <img 
              src={displayUrl} 
              alt={attachment.name} 
              className="max-w-full rounded-lg border border-neutral border-opacity-20"
            />
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity flex items-center justify-center opacity-0 group-hover:opacity-100">
              <a 
                href={displayUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-2 bg-surface-dark bg-opacity-75 rounded-full"
                download
              >
                <Download className="w-5 h-5 text-white" />
              </a>
            </div>
          </div>
        ) : (
          <a 
            href={displayUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center p-3 bg-surface-light rounded-lg hover:bg-surface transition-colors"
            download
          >
            {isPdf ? (
              <FileText className="w-5 h-5 text-secondary-dark mr-2" />
            ) : isText ? (
              <File className="w-5 h-5 text-accent mr-2" />
            ) : (
              <Paperclip className="w-5 h-5 text-neutral mr-2" />
            )}
            <span className="text-sm truncate flex-1">{attachment.name}</span>
            <Download className="w-4 h-4 text-neutral ml-2" />
          </a>
        )}
      </div>
    );
  };

  const PostAttachments = ({ attachments }: { attachments: any[] }) => {
    const { processedAttachments, isLoading } = useAttachments(attachments, 'community');

    if (isLoading) {
      return (
        <div className="mt-2 p-2 bg-surface-light rounded-lg">
          <div className="animate-pulse text-sm text-neutral">Loading attachments...</div>
        </div>
      );
    }

    if (!processedAttachments.length) return null;

    return (
      <div className="space-y-2 mt-2">
        {processedAttachments.map((attachment) => renderAttachment(attachment))}
      </div>
    );
  };

  const fetchReplies = async () => {
    try {
      const { data, error } = await supabase
        .from('post_replies')
        .select(`
          *,
          author:author_id(
            id,
            full_name,
            display_name,
            verified,
            avatar_url
          )
        `)
        .eq('post_id', post.id)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setReplies(data || []);
    } catch (error) {
      console.error('Error fetching replies:', error);
    }
  };

  React.useEffect(() => {
    if (expandedPost) {
      fetchReplies();
    }
  }, [expandedPost]);

  const handleLikeToggle = async () => {
    if (!user) return;

    try {
      setIsLiking(true);
      const { error } = await supabase.rpc('manage_post_likes', {
        post_id: post.id,
        user_id: user.id
      });

      if (error) throw error;

      const hasLiked = localPost.likes?.includes(user.id);
      const updatedPost = {
        ...localPost,
        likes: hasLiked 
          ? localPost.likes.filter((id: string) => id !== user.id)
          : [...(localPost.likes || []), user.id],
        like_count: hasLiked ? Math.max(0, localPost.like_count - 1) : (localPost.like_count || 0) + 1
      };
      setLocalPost(updatedPost);

      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      setLocalPost(post);
    } finally {
      setIsLiking(false);
    }
  };

  const handleEdit = async () => {
    try {
      const { data, error } = await supabase.rpc('manage_post_update', {
        post_id: post.id,
        new_title: editTitle,
        new_content: editContent,
        user_id: user?.id
      });

      if (error) throw error;

      setLocalPost(prev => ({
        ...prev,
        title: editTitle,
        content: editContent,
        updated_at: new Date().toISOString()
      }));

      setIsEditing(false);

      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error updating post:', error);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      setIsDeleting(true);
      const { error } = await supabase
        .from('community_posts')
        .delete()
        .eq('id', post.id);

      if (error) throw error;

      if (onDelete) {
        onDelete();
      }
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error deleting post:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSubmitReply = async () => {
    if (!user || !newReply.trim()) return;

    try {
      const { error } = await supabase
        .from('post_replies')
        .insert({
          post_id: post.id,
          author_id: user.id,
          content: newReply.trim()
        });

      if (error) throw error;

      setNewReply('');
      fetchReplies();
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error submitting reply:', error);
    }
  };

  const handleEditReply = async (replyId: string) => {
    if (!editReplyContent.trim()) return;

    try {
      const { error } = await supabase
        .from('post_replies')
        .update({
          content: editReplyContent.trim(),
          updated_at: new Date().toISOString()
        })
        .eq('id', replyId)
        .eq('author_id', user?.id);

      if (error) throw error;

      setEditingReplyId(null);
      setEditReplyContent('');
      fetchReplies();
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error updating reply:', error);
    }
  };

  const handleDeleteReply = async (replyId: string) => {
    if (!window.confirm('Are you sure you want to delete this reply?')) return;

    try {
      const { error } = await supabase
        .from('post_replies')
        .delete()
        .eq('id', replyId)
        .eq('author_id', user?.id);

      if (error) throw error;

      fetchReplies();
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error deleting reply:', error);
    }
  };

  const handleLikeReply = async (replyId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase.rpc('manage_reply_likes', {
        reply_id: replyId,
        user_id: user.id
      });

      if (error) throw error;

      fetchReplies();
    } catch (error) {
      console.error('Error liking reply:', error);
    }
  };

  const canModify = user && (user.id === post.author_id || user.id === post.community?.leader_id);

  return (
    <div className="bg-surface-light rounded-xl p-4 group hover:bg-neutral">
      <div className="flex flex-col">
        <div className="flex items-center justify-between mb-0">
          <div className="flex items-center gap-4">
            <h3 className="text-xl font-semibold text-primary group-hover:text-surface-light">
              {localPost.title}
            </h3>
          </div>
          
          {canModify && (
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setIsEditing(true)}
                className="p-2 text-neutral group-hover:text-surface-light transition-colors"
                title="Edit post"
              >
                <Edit2 className="w-4 h-4" />
              </button>
              <button
                onClick={handleDelete}
                className="p-2 text-neutral group-hover:text-surface-light transition-colors"
                title="Delete post"
                disabled={isDeleting}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {localPost.community && (
          <div className="text-m text-neutral italic mb-2 group-hover:text-surface-light">
            <Link 
              to={`/communities/${localPost.community.id}`}
              className="text-neutral group-hover:text-primary transition-colors"
            >
              {localPost.community.name}
            </Link>
          </div>
        )}

        <div className="prose prose-sm max-w-none text-primary group-hover:text-surface-light">
          {localPost.content}
        </div>
        
        {/* Render attachments with signed URLs */}
        {localPost.attachments && localPost.attachments.length > 0 && (
          <PostAttachments attachments={localPost.attachments} />
        )}

        <div className="mb-0">
          <UserName
            name={post.author.display_name || post.author.full_name}
            verified={post.author.verified}
            size="sm"
            className="text-neutral group-hover:text-primary"
            userId={post.author.id}
          />
        </div>

        <div className="flex items-center justify-between text-sm text-neutral group-hover:text-surface-light">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleLikeToggle}
              disabled={isLiking || !user}
              className={`flex items-center space-x-1 hover:text-neutral group-hover:text-surface-light transition-colors ${
                localPost.likes?.includes(user?.id) ? 'text-neutral group-hover:text-surface-light fill-current' : ''
              }`}
            >
              <Heart 
                className={`w-4 h-4 ${
                  localPost.likes?.includes(user?.id) ? 'fill-current' : ''
                }`} 
              />
              <span>{localPost.like_count}</span>
            </button>

            <button
              onClick={() => setExpandedPost(!expandedPost)}
              className="flex items-center space-x-1 group-hover:text-surface-light transition-colors"
            >
              <MessageSquare className="w-4 h-4" />
              <span>{localPost.reply_count || 0}</span>
            </button>
          </div>

          <div className="flex flex-col items-end gap-1">
            {localPost.category && (
              <span className="bg-surface-light border-2 border-surface px-1.5 py-0.5 rounded-full text-xs text-neutral">
                {localPost.category.name}
              </span>
            )}
            <span className="text-sm text-neutral group-hover:text-surface-light">
              {format(new Date(localPost.created_at), 'PPp')}
            </span>
          </div>
        </div>
      </div>

      {isEditing && (
        <div className="space-y-4 mt-4">
          <input
            type="text"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-primary-dark text-primary focus:ring-0 focus:outline-none"
            placeholder="Title (optional)"
          />
          <textarea
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-primary-dark text-primary focus:ring-0 focus:outline-none"
            placeholder="Content"
          />
          <div className="flex justify-end space-x-1 mt-1">
            <Button
              variant="outline"
              size="sm"
              className="w-16 h-16 rounded-full text-s bg-secondary-light  border-0 border-transparent text-neutral hover:bg-neutral hover:text-surface-light hover:border-neutral"
              onClick={() => {
                setIsEditing(false);
                setEditContent(localPost.content);
                setEditTitle(localPost.title || '');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              className="w-16 h-16 rounded-full text-s bg-secondary-light  border-0 border-transparent text-neutral hover:bg-neutral hover:text-surface-light hover:border-neutral"
              onClick={handleEdit}
            >
              Save
            </Button>
          </div>
        </div>
      )}

      {expandedPost && (
        <div className="mt-4 space-y-4 border-t border-neutral pt-4">
          {replies.map(reply => (
            <div key={reply.id} className="bg-primary-dark rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-neutral mb-2 group-hover:text-surface-light">
                  <UserName
                    name={reply.author.display_name || reply.author.full_name}
                    verified={reply.author.verified}
                    size="sm"
                    userId={reply.author.id}
                    className="text-neutral group-hover:text-neutral"
                  />
                </span>

                {user?.id === reply.author.id && (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => {
                        setEditingReplyId(reply.id);
                        setEditReplyContent(reply.content);
                      }}
                      className="p-1 text-primary hover:text-base transition-colors"
                      title="Edit reply"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteReply(reply.id)}
                      className="p-1 text-primary hover:text-primary transition-colors"
                      title="Delete reply"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              {editingReplyId === reply.id ? (
                <div className="mb-3">
                  <textarea
                    value={editReplyContent}
                    onChange={(e) => setEditReplyContent(e.target.value)}
                    className="text-primary w-full resize-none bg-transparent border-none focus:outline-none focus:ring-0 px-0 py-0 text-sm"
                    rows={1}
                  />
                  <div className="flex justify-end space-x-1 mt-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-16 h-16 rounded-full text-s bg-secondary-light  border-0 border-transparent text-neutral hover:bg-neutral hover:text-surface-light hover:border-neutral"
                      onClick={() => {
                        setEditingReplyId(null);
                        setEditReplyContent('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      className="w-16 h-16 rounded-full text-s bg-secondary-light  border-0 border-transparent text-neutral hover:bg-neutral hover:text-surface-light hover:border-neutral"
                      onClick={() => handleEditReply(reply.id)}
                    >
                      Save
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col">
                  <div className="text-primary mb-3">
                    {reply.content}
                  </div>

                  <div className="flex items-center justify-between text-sm text-primary">
                    <button
                      onClick={() => handleLikeReply(reply.id)}
                      className={`flex items-center space-x-1 hover:text-primary transition-colors ${
                        user && reply.likes?.includes(user.id) ? 'text-primary fill-current' : ''
                      }`}
                    >
                      <Heart 
                        className={`w-4 h-4 ${
                          user && reply.likes?.includes(user.id) ? 'fill-current' : ''
                        }`}
                      />
                      <span>{reply.like_count}</span>
                    </button>
                    <span className="text-sm text-primary">
                      {format(new Date(reply.created_at), 'PPp')}
                    </span>
                  </div>
                </div>
              )}
            </div>
          ))}

          {user && (
            <div className="flex flex-wrap sm:flex-nowrap items-center gap-2">
              <input
                type="text"
                value={newReply}
                onChange={(e) => setNewReply(e.target.value)}
                placeholder="Write a comment..."
                className="h-10 flex-1 min-w-0 pl-4 bg-primary-dark rounded-full px-4 py-2 text-primary placeholder:text-primary focus:outline-none focus:ring-0"
              />
              <Button
                variant="primary"
                size="sm"
                onClick={handleSubmitReply}
                disabled={!newReply.trim()}
                className="h-10 bg-secondary-light text-center border-0 border-transparent text-neutral hover:bg-neutral hover:text-surface-light hover:border-neutral 
             w-24 px-3 py-1 text-xs flex items-center justify-center gap-1"
              >
                <Send className="w-3 h-3" />
                <span>Send</span>
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}